import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { AppHeader } from '@/features/layout/AppHeader';
import { Footer } from '@/features/layout/Footer';
import { LoadingScreen } from '@/features/layout/LoadingScreen';
import { HttpError, setFetchClientTokenGetter } from '@/lib/fetchClient';
import { setAuthTokenGetter as setWSAuthTokenGetter } from '@/features/chat/services/websocketChatService';
import { meQueryOptions } from '@/features/auth/useCurrentUser';
import { releaseNotesQueryOptions } from '@/features/help-center/release-notes/queryOptions';
import { HTTP_STATUS } from '@/constants';
import { useEffect } from 'react';
import { AUTH_REDIRECT_KEY } from '@/constants';
import { AppNav } from '@/features/layout/AppNav.tsx';

/**
 * Pathless layout wrapping all authenticated app pages.
 *
 * beforeLoad:
 *   - Always wires token getters early so the loader fetch is authenticated.
 *   - Redirects to /sign-in when the user is definitively not authenticated
 *     (isLoading=false guard prevents redirecting during MSAL startup).
 *   - Saves the intended path to sessionStorage for post-login deep-link
 *     restoration via InnerApp's useEffect + router.history.replace.
 *
 * loader:
 *   - Permission probe (GET /api/version): 403 → /403, 5xx → /500.
 *   - Skipped while not yet authenticated.
 *
 * component:
 *   - Full app shell: AppHeader + AppNav + <Outlet> + Footer.
 *   - Shows a loading screen while MSAL is initialising.
 *   - useEffect handles the edge case where beforeLoad ran with isLoading=true
 *     but MSAL then settled to unauthenticated.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context, location }) => {
    // Wire token getters synchronously so the loader fetch has auth headers.
    setFetchClientTokenGetter(context.auth.getAccessToken);
    setWSAuthTokenGetter(context.auth.getAccessToken);

    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      // Persist the deep link — skip "/" and "/sign-in" (not meaningful targets).
      const path = location.pathname;
      if (path !== '/' && !path.startsWith('/sign-in')) {
        sessionStorage.setItem(AUTH_REDIRECT_KEY, location.href);
      }
      throw redirect({ to: '/sign-in' });
    }
    // isLoading=true: fall through — component renders <LoadingScreen>.
  },

  loader: async ({ context }) => {
    // Skip while MSAL is still processing — token is not yet available.
    if (!context.auth.isAuthenticated) return;

    // Warm the release-notes cache so the header indicator is ready on load.
    // Fire-and-forget: prefetchQuery never rejects, so a release-notes failure
    // can't block app load or trip the error redirects below.
    void context.queryClient.prefetchQuery(releaseNotesQueryOptions());

    try {
      await context.queryClient.ensureQueryData(meQueryOptions());
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.response.status === HTTP_STATUS.UNAUTHORIZED) {
          // Token issue — route to sign-in rather than the generic error page.
          throw redirect({ to: '/sign-in' });
        }
        if (error.response.status === HTTP_STATUS.FORBIDDEN) {
          throw redirect({ to: '/403' });
        }
        if (error.response.status >= HTTP_STATUS.SERVER_ERROR) {
          throw redirect({ to: '/500' });
        }
      }
      // Network / unknown error — treat as server error.
      throw redirect({ to: '/500' });
    }
  },

  component: AppLayout,
});

function AppLayout() {
  const { auth } = Route.useRouteContext();
  const navigate = useNavigate();

  // Re-wire on every render so a refreshed getAccessToken is always current.
  setFetchClientTokenGetter(auth.getAccessToken);
  setWSAuthTokenGetter(auth.getAccessToken);

  // Edge case: beforeLoad ran with isLoading=true (fell through), but MSAL
  // then settled to unauthenticated. Drive the redirect from the component.
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      const path = window.location.pathname;
      if (path !== '/' && !path.startsWith('/sign-in')) {
        // Use the full relative URL (pathname + search + hash) so the format
        // matches what beforeLoad stores via location.href, ensuring
        // router.history.replace() in InnerApp works correctly.
        sessionStorage.setItem(
          AUTH_REDIRECT_KEY,
          window.location.pathname + window.location.search + window.location.hash
        );
      }
      navigate({ to: '/sign-in', replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  if (auth.isLoading) {
    return <LoadingScreen message='Loading...' />;
  }

  return (
    <div className='flex flex-col h-screen w-screen overflow-hidden'>
      <AppHeader />
      <div className='flex flex-1 min-h-0 overflow-hidden'>
        <AppNav />
        <div className='flex flex-col flex-1 min-w-0 overflow-hidden bg-talent-surface'>
          <Outlet />
          <Footer className='px-0' />
        </div>
      </div>
    </div>
  );
}
